const noop = () => {}

/**
 * A helper function to create a development only logger instance.
 *
 * @param name The name with which to label log lines
 * @param color The hex code with which to differentiate the plugin logging
 * @param env The env for which the logger is being created
 * @returns A logger with which to log in development but not prod.
 */
export const buildDevOnlyLogger = (options: {
  name: string
  color: string
  env: string
}) => {
  /**
   * Helper function to decorate a logging method with extra functionality
   * @param consoleMethod The target method to decorate
   * @returns The decorated function
   */
  const createMethodFor = (
    consoleMethod: Console['info'] | Console['warn'] | Console['error'],
    msgPrefix: string
  ) => {
    if (options.env === 'production') {
      return noop
    }
    return (...args: Array<any>) =>
      consoleMethod(
        `%c ${options.name} `,
        `color:#ffffff;background:${options.color};font-weight:bold;padding:1px;border-radius:4px;`,
        msgPrefix,
        ...args
      )
  }

  /* eslint-disable no-console */
  return {
    info: createMethodFor(console.info, '[info]'),
    warn: createMethodFor(console.warn, '[warn]'),
    error: createMethodFor(console.error, '[error]'),
  }
  /* eslint-enable no-console */
}

/**
 * A helper function to create a development only plugin logger.
 *
 * @param name The name of the plugin with which to label log lines
 * @param color The hex code with which to differentiate the plugin logging
 * @param env The env for which the logger is being created
 * @returns A plugin logger with which to log in development but not prod.
 */
export const buildPluginLogger = (options: {
  name: string
  color: string
  env: string
}) =>
  buildDevOnlyLogger({
    ...options,
    name: `plugins/${options.name}`,
  })
